var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Stack, Typography } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { PREVIEW_SCHEDULED_OUTCOME_CHANGE, } from "@/queries/Lifecycle";
import { OUTCOME_KEY, ScheduledOutcome } from "@/components/timezones/ScheduledOutcome";
import { EScheduledOutcomeKind } from "@sequentech/ui-core";
/**
 * Before saving a scheduled opening or closing: what saving does to its
 * outcome (design §5c), e.g. that an edit takes it out of the signed
 * configuration, with the new outcome's "Why?". Nothing is shown when
 * saving changes no outcome.
 */
export var OutcomeChangeNotice = function (_a) {
    var _b, _c;
    var electionEventId = _a.electionEventId, change = _a.change, zone = _a.zone;
    var data = useQuery(PREVIEW_SCHEDULED_OUTCOME_CHANGE, {
        variables: { electionEventId: electionEventId, change: { scheduled_event: change } },
        fetchPolicy: "network-only",
    }).data;
    var changes = (_c = (_b = data === null || data === void 0 ? void 0 : data.preview_scheduled_outcome_change) === null || _b === void 0 ? void 0 : _b.changes) !== null && _c !== void 0 ? _c : [];
    return _jsx(OutcomeChanges, { changes: changes, zone: zone });
};
export var OutcomeChanges = function (_a) {
    var changes = _a.changes, zone = _a.zone;
    var t = useTranslation().t;
    if (!changes.length)
        return null;
    var chip = function (explanation) {
        return t("scheduledOutcome.chip.".concat(OUTCOME_KEY[explanation.outcome]));
    };
    // An event-wide row changes at every election: one notice, grouped by reason.
    var groups = groupChanges(changes);
    var refused = changes.some(function (_a) {
        var after = _a.after;
        return after.outcome === EScheduledOutcomeKind.REFUSED;
    });
    return (_jsxs(Alert, { severity: refused ? "warning" : "info", sx: { width: "100%" }, "data-testid": "outcome-change-notice", children: [changes.length > 1 ? (_jsx(Typography, { variant: "body2", sx: { mb: 1 }, children: t("lifecycle.schedule.outcomeChangeElections", { count: changes.length }) })) : null, _jsx(Stack, { spacing: 1, children: groups.map(function (group) {
                    var _a;
                    var _b = group[0], before = _b.before, after = _b.after;
                    return (_jsxs(Stack, { spacing: 0.5, children: [_jsxs(Typography, { variant: "body2", children: [before
                                        ? t("lifecycle.schedule.outcomeChange", {
                                            before: chip(before),
                                            after: chip(after),
                                        })
                                        : t("lifecycle.schedule.outcomeNew", { after: chip(after) }), changes.length > 1
                                        ? " ".concat(t("lifecycle.schedule.outcomeElections", {
                                            count: group.length,
                                            total: changes.length,
                                        }))
                                        : null] }), _jsx(ScheduledOutcome, { explanation: after, zone: zone })] }, "".concat((_a = before === null || before === void 0 ? void 0 : before.outcome) !== null && _a !== void 0 ? _a : "new", "-").concat(after.outcome, "-").concat(after.deciding)));
                }) })] }));
};
/** Changes that read the same: the same outcome before and after, decided by the same check. */
export var groupChanges = function (changes) {
    var _a, _b, _c;
    var groups = new Map();
    for (var _i = 0, changes_1 = changes; _i < changes_1.length; _i++) {
        var change = changes_1[_i];
        var key = "".concat((_b = (_a = change.before) === null || _a === void 0 ? void 0 : _a.outcome) !== null && _b !== void 0 ? _b : "new", "|").concat(change.after.outcome, "|").concat(change.after.deciding);
        groups.set(key, __spreadArray(__spreadArray([], ((_c = groups.get(key)) !== null && _c !== void 0 ? _c : []), true), [change], false));
    }
    return Array.from(groups.values());
};
